"use client";

import { useState, type CSSProperties } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Chrome } from "@/components/ui/tls/chrome";
import { Mono } from "@/components/ui/tls/mono";
import { ModelBadge } from "@/components/ui/tls/model-badge";
import { formatInstallCountDetail } from "@/lib/format-count";
import { fmtDurationPrecise, fmtFileSize } from "@/lib/tls-format";
import { cn } from "@/lib/utils";
import { TESLA_FACTS } from "@/lib/tesla-facts";
import { type Sound } from "@/lib/supabase";
import { PostDownloadPanel } from "@/components/ui/post-download-panel";

// TES-831: the wizard sits in a below-fold accordion — code-splitting it out
// keeps it off the initial JS bundle exactly as in sound-player-client.tsx.
const InstallationWizard = dynamic(
  () => import("@/components/ui/installation-wizard"),
  { loading: () => null }
);

export interface SpecSheetPanelProps {
  sound: Sound;
  liveDownloads: number;
  downloadCompleted: boolean;
  downloadError: string | null;
  installHint: string | null;
  onDownload: () => void;
  onShare: () => void;
  onCreateShareCard: () => void;
  /**
   * TES-1238: on the sound-detail page the inline <PostDownloadReceipt/> is now
   * the single post-download surface, so the sidebar's fuller PostDownloadPanel
   * is suppressed to avoid a duplicate confirmation + install-step block. Other
   * consumers (e.g. Chime Studio) keep it by leaving this at its default.
   */
  showPostDownloadPanel?: boolean;
}

/**
 * Ruling C: attribution values are prose in the one family — body scale,
 * body weight (500 as of ruling A), ink-meta tone. Body-sm rather than body
 * because the credit sits at the foot of a dense sidebar column; it is the
 * body ROLE, one step down the ladder, not the label role.
 */
const attributionValueStyle: CSSProperties = {
  font: "var(--tls-body-sm)",
  color: "var(--tls-fg-subtle)",
};

/**
 * Right-column spec sheet panel extracted from sound-player-client.tsx.
 * The caller keeps the outer `m.div variants={itemVariants}` wrapper; this
 * component renders the blocks that live inside it, byte-identical.
 */
export function SpecSheetPanel({
  sound,
  liveDownloads,
  downloadCompleted,
  downloadError,
  installHint,
  onDownload,
  onShare,
  onCreateShareCard,
  showPostDownloadPanel = true,
}: SpecSheetPanelProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>("install");
  const rm = useReducedMotion();

  const compatibleModels =
    sound.compatible_models && sound.compatible_models.length > 0
      ? sound.compatible_models
      : ["S", "3", "X", "Y", "CT"];
  const allModels = ["S", "3", "X", "Y", "CT"];
  // F-8: the SOURCE label is followed by the value; some catalog rows store
  // the value already prefixed ("Source: Iron Man…"), which doubled the word.
  const sourceReference = sound.source_reference
    ?.replace(/^\s*source\s*[:\-\u2013\u2014]\s*/i, "")
    .trim();

  return (
    <>
      <aside
        style={{
          paddingTop: 20,
          borderTop: "1px solid var(--tls-border-subtle)",
        }}
      >
        <div className="tlsr-section-tick" style={{ marginBottom: 14 }}>
          <Chrome>spec sheet</Chrome>
        </div>

        {/* Spec sheet (§5.5). Duration · Size · Downloads with a
            Details disclosure for format/rate/bit-depth. */}
        {[
          { l: "duration", v: sound.duration ? fmtDurationPrecise(sound.duration) : "N/A" },
          { l: "file size", v: fmtFileSize(sound.size) || "N/A" },
          // Same zero-state phrase as the meta row above the title
          // ("— not installed yet"), not the list rows' "NEW": one template,
          // one way of saying zero. Non-zero keeps the bare number because
          // the row label already carries the unit.
          {
            l: "downloads",
            v: (() => {
              const { value, unit, isZero } = formatInstallCountDetail(liveDownloads);
              return isZero ? `${value} ${unit.toLowerCase()}` : value;
            })(),
          },
        ].map(row => (
          <div
            key={row.l}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 0",
              borderBottom: "1px solid var(--tls-border-subtle)",
            }}
          >
            <Chrome>{row.l}</Chrome>
            <Mono size={12} color="var(--tls-fg-strong)">
              {row.v}
            </Mono>
          </div>
        ))}

        {/* Details disclosure (§5.5) */}
        <details className="group tlsr-details-animate" style={{ marginTop: 4 }}>
            {/* eslint-disable-next-line react-doctor/no-tiny-text -- details disclosure label intentionally uses 11px */}
            <summary
              className="cursor-pointer select-none list-none flex items-center justify-between py-2"
              style={{ color: "var(--tls-fg-muted)", fontSize: 11 }}
            >
              <Chrome>details</Chrome>
              <svg
                className="size-3 transition-transform group-open:rotate-180"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </summary>
            <div>
              {[
                { l: "format", v: (sound.format?.toUpperCase() || "WAV") + " / PCM" },
                { l: "sample rate", v: "44.1 kHz" },
                { l: "bit depth", v: "16-bit" },
              ].map(row => (
                <div
                  key={row.l}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid var(--tls-border-subtle)",
                  }}
                >
                  <Chrome>{row.l}</Chrome>
                  <Mono size={12} color="var(--tls-fg-strong)">{row.v}</Mono>
                </div>
              ))}
              {sound.is_normalized && (
                <div style={{ marginTop: 10 }}>
                  <Link
                    href="/guide#volume"
                    className="inline-flex text-xs font-semibold no-underline underline decoration-[color:var(--tls-border-strong)] underline-offset-4 transition-colors hover:text-[var(--tls-brand-ink)]"
                    style={{
                      color: "var(--tls-fg-muted)",
                    }}
                  >
                    Volume normalized
                  </Link>
                </div>
              )}
              {sound.creator_name && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--tls-border-subtle)" }}>
                  <Chrome>submitted by</Chrome>
                  {/* Ruling C: a person's name is prose, not a spec value. */}
                  <span style={{ ...attributionValueStyle, display: "block", marginTop: 4 }}>
                    {sound.creator_name}
                  </span>
                </div>
              )}
            </div>
          </details>

        {/* Compatible models (§5.6). Factual compatibility remains inline;
            status is not a green success chip. */}
        <Chrome style={{ display: "block", margin: "20px 0 10px" }}>compatible models</Chrome>
        {compatibleModels.length === 5 ? (
          <Mono size={12} color="var(--tls-fg-strong)">
            {TESLA_FACTS.compatibility.short}
          </Mono>
        ) : (
          <>
            <Mono size={11} color="var(--tls-fg-subtle)" style={{ display: "block", marginBottom: 8 }}>
              Works on {compatibleModels.length} Tesla model{compatibleModels.length !== 1 ? "s" : ""}
            </Mono>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {allModels.map(m => (
                <span
                  key={m}
                  style={!compatibleModels.includes(m) ? { opacity: 0.4 } : undefined}
                >
                  <ModelBadge model={m} active={compatibleModels.includes(m)} />
                </span>
              ))}
            </div>
          </>
        )}

        {/* Install hint is a factual annotation, not a nested card, and not
            a ruled callout either: the DS refuses borders on its own content
            (the ad quarantine is the one exception). Its separation is the
            row spacing above and the ink-muted tone. */}
        <div
          style={{
            marginTop: 20,
            padding: "2px 0",
          }}
        >
          <div>
            <p
              style={{
                font: "var(--tls-body-sm)",
                color: "var(--tls-fg-muted)",
                margin: 0,
                lineHeight: 1.55,
              }}
            >
              Drop{" "}
              {/* eslint-disable-next-line react-doctor/no-tiny-text -- inline code path uses 11.5px mono font intentionally */}
              <code
                style={{
                  fontFamily: "var(--tls-font-mono)",
                  fontSize: 11.5,
                  color: "var(--tls-fg-strong)",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {TESLA_FACTS.usb.path}
              </code>{" "}
              on your USB stick, plug into the front USB data port, lock the car. Done in {TESLA_FACTS.install.label}.
            </p>
          </div>
          {/* eslint-disable-next-line react-doctor/no-inline-exhaustive-style -- uses CSS custom properties and color-mix() not expressible in Tailwind */}
          <Link
            href="/guide"
            style={{
              marginTop: 10,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              color: "var(--tls-fg-strong)",
              fontSize: 12,
              fontWeight: 600,
              textDecoration: "none",
              borderBottom:
                "1px solid color-mix(in srgb, var(--tls-fg-strong) 30%, transparent)",
              paddingBottom: 1,
            }}
          >
            Read full install guide
          </Link>
        </div>

        {/* Download error: verdict in ink at 700, no semantic red */}
        {downloadError && (
          <p className="mt-3 text-sm text-center font-bold" style={{ color: "var(--tls-fg-strong)" }}>
            {downloadError}
          </p>
        )}

        {/* Post-download CTAs */}
        {downloadCompleted && showPostDownloadPanel && (
          <PostDownloadPanel
            soundId={sound.id}
            soundTitle={sound.title}
            className="mt-4"
            onShare={onShare}
            onCreateShareCard={onCreateShareCard}
          />
        )}
      </aside>

      {/* How to Install accordion */}
      <div className="mt-4">
        <div
          style={{
            // De-carded: an accordion is content, and its own edge duplicated
            // the separation the surrounding rows already provide. One hairline
            // above it does the same job as four plus a radius.
            overflow: "hidden",
            borderTop: "1px solid var(--tls-border-subtle)",
          }}
        >
          <button type="button"
            onClick={() =>
              setExpandedSection(expandedSection === "install" ? null : "install")
            }
            aria-expanded={expandedSection === "install"}
            aria-controls="install-accordion-panel"
            className="flex w-full items-center justify-between px-5 py-4 text-left transition-[background-color]"
            style={{ background: "transparent" }}
          >
            <div>
              <span style={{ fontWeight: 600, color: "var(--tls-fg-strong)", fontSize: 14 }}>
                How to install
              </span>
            </div>
            <ChevronDown
              className={cn(
                "size-5 transition-transform duration-[var(--tls-duration-smooth)]",
                expandedSection === "install" && "rotate-180"
              )}
              style={{ color: "var(--tls-fg-muted)" }}
            />
          </button>
          <AnimatePresence>
            {expandedSection === "install" && (
              <m.div
                id="install-accordion-panel"
                initial={rm ? { opacity: 0 } : { height: 0, opacity: 0 }}
                animate={rm ? { opacity: 1 } : { height: "auto", opacity: 1 }}
                exit={rm ? { opacity: 0 } : { height: 0, opacity: 0 }}
                transition={rm ? { duration: 0 } : { duration: 0.3 }}
                className="overflow-hidden"
                style={{ borderTop: "1px solid var(--tls-border-subtle)" }}
              >
                <div className="p-5">
                  {/* Pre-step hint (§5.7) */}
                  {installHint && (
                    <div
                      className="mb-4 flex items-start gap-2 py-2 text-xs font-semibold"
                      style={{
                        // An annotation: weight and ink-muted tone carry it.
                        // No rule (the DS refuses borders on its own content)
                        // and no colour to encode a warning state.
                        color: "var(--tls-fg-muted)",
                      }}
                    >
                      {installHint}
                    </div>
                  )}
                  <InstallationWizard
                    soundTitle={sound.title}
                    soundFormat={sound.format}
                    soundSize={sound.size}
                    onDownloadClick={onDownload}
                  />
                </div>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* Creator / source credit (§5.8).
          Owner ruling C (2026-08-14): this block is prose, not code. It used
          to set five lines of a person's name and a source citation in the
          monospace meta face, which read as a machine readout of something
          a human wrote. The label lines stay on the 11px label token
          (<Chrome>); the values are body-scale prose in ink-meta. */}
      {(sound.creator_name || sourceReference) && (
        <div
          className="mt-4 py-3"
          style={{
            borderTop: "1px solid var(--tls-border-subtle)",
          }}
        >
          {sound.creator_name && (
            <div>
              <Chrome>submitted by</Chrome>{" "}
              <span style={attributionValueStyle}>{sound.creator_name}</span>
            </div>
          )}
          {sourceReference && (
            <div style={{ marginTop: sound.creator_name ? 4 : 0 }}>
              <Chrome>source</Chrome>{" "}
              <span style={attributionValueStyle}>{sourceReference}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
